package co.maytes.standupbot.dashboard.components.widgets.icons

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.widgets.svg.Svg
import co.maytes.standupbot.dashboard.components.widgets.svg.SvgCircle
import co.maytes.standupbot.dashboard.components.widgets.svg.SvgPath

@Composable
fun LucideIcon(
    paths: List<String>,
    size: Int = 20,
    strokeColor: String = "currentColor",
    strokeWidth: Double = 2.0,
    circles: List<Triple<Double, Double, Double>> = emptyList(),
) {
    Svg(attrs = {
        attr("viewBox", "0 0 24 24")
        attr("width", size.toString())
        attr("height", size.toString())
        attr("fill", "none")
        attr("stroke", strokeColor)
        attr("stroke-width", strokeWidth.toString())
        attr("stroke-linecap", "round")
        attr("stroke-linejoin", "round")
        attr("aria-hidden", "true")
    }) {
        paths.forEach { d -> SvgPath(attrs = { attr("d", d) }) }
        circles.forEach { (cx, cy, r) ->
            SvgCircle(attrs = {
                attr("cx", cx.toString())
                attr("cy", cy.toString())
                attr("r", r.toString())
            })
        }
    }
}

@Composable
fun LucideHouse(size: Int = 20, strokeColor: String = "currentColor") =
    LucideIcon(
        size = size,
        strokeColor = strokeColor,
        paths = listOf(
            "M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8",
            "M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z",
        ),
    )

@Composable
fun LucideMessagesSquare(size: Int = 20, strokeColor: String = "currentColor") =
    LucideIcon(
        size = size,
        strokeColor = strokeColor,
        paths = listOf(
            "M14 9a2 2 0 0 1-2 2H6l-4 4V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2z",
            "M18 9h2a2 2 0 0 1 2 2v11l-4-4h-6a2 2 0 0 1-2-2v-1",
        ),
    )

@Composable
fun LucideUsers(size: Int = 20, strokeColor: String = "currentColor") =
    LucideIcon(
        size = size,
        strokeColor = strokeColor,
        paths = listOf(
            "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2",
            "M22 21v-2a4 4 0 0 0-3-3.87",
            "M16 3.13a4 4 0 0 1 0 7.75",
        ),
        circles = listOf(Triple(9.0, 7.0, 4.0)),
    )

@Composable
fun LucideFileText(size: Int = 20, strokeColor: String = "currentColor") =
    LucideIcon(
        size = size,
        strokeColor = strokeColor,
        paths = listOf(
            "M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z",
            "M14 2v4a2 2 0 0 0 2 2h4",
            "M10 9H8",
            "M16 13H8",
            "M16 17H8",
        ),
    )

@Composable
fun LucideBarChart(size: Int = 20, strokeColor: String = "currentColor") =
    LucideIcon(
        size = size,
        strokeColor = strokeColor,
        paths = listOf(
            "M3 3v16a2 2 0 0 0 2 2h16",
            "M18 17V9",
            "M13 17V5",
            "M8 17v-3",
        ),
    )

@Composable
fun LucideSettings(size: Int = 20, strokeColor: String = "currentColor") =
    LucideIcon(
        size = size,
        strokeColor = strokeColor,
        paths = listOf(
            "M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z",
        ),
        circles = listOf(Triple(12.0, 12.0, 3.0)),
    )

@Composable
fun LucideMenu(size: Int = 20, strokeColor: String = "currentColor") =
    LucideIcon(
        size = size,
        strokeColor = strokeColor,
        paths = listOf(
            "M4 6h16",
            "M4 12h16",
            "M4 18h16",
        ),
    )

@Composable
fun LucideX(size: Int = 20, strokeColor: String = "currentColor") =
    LucideIcon(
        size = size,
        strokeColor = strokeColor,
        paths = listOf(
            "M18 6 6 18",
            "M6 6l12 12",
        ),
    )

@Composable
fun LucideCalendar(size: Int = 20, strokeColor: String = "currentColor") =
    LucideIcon(
        size = size,
        strokeColor = strokeColor,
        paths = listOf(
            "M8 2v4",
            "M16 2v4",
            "M3 10h18",
            "M21 6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2z",
        ),
    )

@Composable
fun LucideClock(size: Int = 20, strokeColor: String = "currentColor") =
    LucideIcon(
        size = size,
        strokeColor = strokeColor,
        paths = listOf("M12 6v6l4 2"),
        circles = listOf(Triple(12.0, 12.0, 10.0)),
    )

@Composable
fun LucideTag(size: Int = 20, strokeColor: String = "currentColor") =
    LucideIcon(
        paths = listOf("M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z"),
        circles = listOf(Triple(7.5, 7.5, 0.5)),
        size = size,
        strokeColor = strokeColor,
    )

@Composable
fun LucidePlay(size: Int = 20, strokeColor: String = "currentColor") =
    LucideIcon(
        size = size,
        strokeColor = strokeColor,
        paths = listOf("M6 3l14 9-14 9Z"),
    )

@Composable
fun LucideTrash(size: Int = 20, strokeColor: String = "currentColor") =
    LucideIcon(
        size = size,
        strokeColor = strokeColor,
        paths = listOf(
            "M3 6h18",
            "M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6",
            "M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2",
            "M10 11v6",
            "M14 11v6",
        ),
    )

@Composable
fun LucideArrowLeft(size: Int = 20, strokeColor: String = "currentColor") =
    LucideIcon(
        size = size,
        strokeColor = strokeColor,
        paths = listOf(
            "M19 12H5",
            "M12 19l-7-7 7-7",
        ),
    )

@Composable
fun LucideChevronDown(size: Int = 20, strokeColor: String = "currentColor") =
    LucideIcon(
        size = size,
        strokeColor = strokeColor,
        paths = listOf("m6 9 6 6 6-6"),
    )

@Composable
fun LucideEye(size: Int = 20, strokeColor: String = "currentColor") =
    LucideIcon(
        size = size,
        strokeColor = strokeColor,
        paths = listOf("M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"),
        circles = listOf(Triple(12.0, 12.0, 3.0)),
    )

@Composable
fun LucideEyeOff(size: Int = 20, strokeColor: String = "currentColor") =
    LucideIcon(
        size = size,
        strokeColor = strokeColor,
        paths = listOf(
            "M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49",
            "M14.084 14.158a3 3 0 0 1-4.242-4.242",
            "M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143",
            "m2 2 20 20",
        ),
    )

@Composable
fun LucideCheck(size: Int = 20, strokeColor: String = "currentColor") =
    LucideIcon(
        size = size,
        strokeColor = strokeColor,
        paths = listOf("M20 6 9 17l-5-5"),
    )

@Composable
fun LucideAlertTriangle(size: Int = 20, strokeColor: String = "currentColor") =
    LucideIcon(
        size = size,
        strokeColor = strokeColor,
        paths = listOf(
            "m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3",
            "M12 9v4",
            "M12 17h.01",
        ),
    )

@Composable
fun LucideAlertCircle(size: Int = 20, strokeColor: String = "currentColor") =
    LucideIcon(
        size = size,
        strokeColor = strokeColor,
        paths = listOf(
            "M12 8v4",
            "M12 16h.01",
        ),
        circles = listOf(Triple(12.0, 12.0, 10.0)),
    )

@Composable
fun LucideSearch(size: Int = 20, strokeColor: String = "currentColor") =
    LucideIcon(
        size = size,
        strokeColor = strokeColor,
        paths = listOf("m21 21-4.34-4.34"),
        circles = listOf(Triple(11.0, 11.0, 8.0)),
    )

@Composable
fun LucideUser(size: Int = 20, strokeColor: String = "currentColor") =
    LucideIcon(
        size = size,
        strokeColor = strokeColor,
        paths = listOf("M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"),
        circles = listOf(Triple(12.0, 7.0, 4.0)),
    )

@Composable
fun LucideBuilding(size: Int = 20, strokeColor: String = "currentColor") =
    LucideIcon(
        size = size,
        strokeColor = strokeColor,
        paths = listOf(
            "M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z",
            "M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2",
            "M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2",
            "M10 6h4",
            "M10 10h4",
            "M10 14h4",
            "M10 18h4",
        ),
    )

@Composable
fun LucidePlug(size: Int = 20, strokeColor: String = "currentColor") =
    LucideIcon(
        size = size,
        strokeColor = strokeColor,
        paths = listOf(
            "M12 22v-5",
            "M9 8V2",
            "M15 8V2",
            "M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8Z",
        ),
    )

@Composable
fun LucideCreditCard(size: Int = 20, strokeColor: String = "currentColor") =
    LucideIcon(
        size = size,
        strokeColor = strokeColor,
        paths = listOf(
            "M2 10h20",
            "M22 6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2z",
        ),
    )

@Composable
fun LucideScrollText(size: Int = 20, strokeColor: String = "currentColor") =
    LucideIcon(
        size = size,
        strokeColor = strokeColor,
        paths = listOf(
            "M15 12h-5",
            "M15 8h-5",
            "M19 17V5a2 2 0 0 0-2-2H4",
            "M8 21h12a2 2 0 0 0 2-2v-1a1 1 0 0 0-1-1H11a1 1 0 0 0-1 1v1a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v2a1 1 0 0 0 1 1h3",
        ),
    )

@Composable
fun LucideTerminal(size: Int = 20, strokeColor: String = "currentColor") =
    LucideIcon(
        size = size,
        strokeColor = strokeColor,
        paths = listOf(
            "M12 19h8",
            "m4 17 6-6-6-6",
        ),
    )

@Composable
fun LucideMoon(size: Int = 20, strokeColor: String = "currentColor") =
    LucideIcon(
        size = size,
        strokeColor = strokeColor,
        paths = listOf(
            "M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z",
        ),
    )

@Composable
fun LucideSun(size: Int = 20, strokeColor: String = "currentColor") =
    LucideIcon(
        size = size,
        strokeColor = strokeColor,
        paths = listOf(
            "M12 2v2",
            "M12 20v2",
            "M4.93 4.93l1.41 1.41",
            "M17.66 17.66l1.41 1.41",
            "M2 12h2",
            "M20 12h2",
            "M6.34 17.66l-1.41 1.41",
            "M19.07 4.93l-1.41 1.41",
        ),
        circles = listOf(Triple(12.0, 12.0, 4.0)),
    )

@Composable
fun LucideList(size: Int = 20, strokeColor: String = "currentColor") =
    LucideIcon(
        size = size,
        strokeColor = strokeColor,
        paths = listOf(
            "M8 6h13",
            "M8 12h13",
            "M8 18h13",
            "M3 6h.01",
            "M3 12h.01",
            "M3 18h.01",
        ),
    )

@Composable
fun LucideShield(size: Int = 20, strokeColor: String = "currentColor") =
    LucideIcon(
        size = size,
        strokeColor = strokeColor,
        paths = listOf(
            "M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z",
        ),
    )

@Composable
fun LucideLock(size: Int = 20, strokeColor: String = "currentColor") =
    LucideIcon(
        size = size,
        strokeColor = strokeColor,
        paths = listOf(
            "M5 11h14a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2z",
            "M7 11V7a5 5 0 0 1 10 0v4",
        ),
    )
